import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import AuthSplitLayout, { authStyles as styles } from "../../components/auth/AuthSplitLayout";
import {
  AuthField,
  AuthFormError,
  AuthPasswordField,
} from "../../components/auth/AuthFormFields";
import { resolveAuthVariant } from "../../lib/authPortal";

function resolveNext(loc, variant) {
  const params = new URLSearchParams(loc.search || "");
  const fromQuery = params.get("next");
  const fromState = loc.state?.next;
  if (fromState) return fromState;
  if (fromQuery) {
    try {
      return decodeURIComponent(fromQuery);
    } catch (_) {
      return fromQuery;
    }
  }
  return variant === "admin" ? "/admin" : "/dashboard";
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const variant = useMemo(
    () =>
      resolveAuthVariant(
        typeof window !== "undefined" ? window.location.hostname : "",
        loc.search
      ),
    [loc.search]
  );
  const isAdmin = variant === "admin";

  const [email, setEmail] = useState(isAdmin ? "admin@premierlab.ai" : "");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const validate = () => {
    const next = {};
    if (!email.trim()) next.email = "Enter your email address.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      next.email = "Enter a valid email address.";
    }
    if (!password) next.password = "Enter your password.";
    else if (password.length < 6) {
      next.password = "Password must be at least 6 characters.";
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!validate()) return;
    setBusy(true);
    try {
      await login(email.trim(), password);
      nav(resolveNext(loc, variant), { replace: true });
    } catch (e2) {
      setErr(e2.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthSplitLayout
      variant={variant}
      eyebrow={isAdmin ? "Admin access" : "Welcome back"}
      title={isAdmin ? "Sign in to PremierLab Admin" : "Sign in to PremierLab"}
      subtitle={
        isAdmin
          ? "Continue to the PremierLab Control Center to manage the platform."
          : "Continue building your projects, campaigns, and AI-powered content."
      }
      formTestId={isAdmin ? "admin-login-panel" : "user-login-panel"}
    >
      <form
        onSubmit={submit}
        data-testid={isAdmin ? "admin-login-form" : "user-login-form"}
        noValidate
      >
        <AuthFormError message={err} testId={isAdmin ? "admin-login-error" : "user-login-error"} />

        <AuthField
          id="login-email"
          label="Email address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          autoFocus
          error={fieldErrors.email}
          testId={isAdmin ? "admin-login-email" : "user-login-email"}
          required
        />

        <AuthPasswordField
          id="login-password"
          label="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter your password"
          autoComplete="current-password"
          error={fieldErrors.password}
          testId={isAdmin ? "admin-login-password" : "user-login-password"}
          toggleTestId={isAdmin ? "admin-login-password-toggle" : "user-login-password-toggle"}
          required
        />

        <button
          className={styles.primary}
          data-testid={isAdmin ? "admin-login-submit" : "user-login-submit"}
          disabled={busy}
          type="submit"
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>

        {!isAdmin && (
          <div className={styles.below} data-testid="user-login-signup-link">
            Don&apos;t have an account? <Link to="/signup">Sign up for free</Link>
          </div>
        )}

        {isAdmin && (
          <div className={styles.legal} data-testid="admin-login-note">
            Restricted to authorized PremierLab operators.
          </div>
        )}
      </form>
    </AuthSplitLayout>
  );
}
